import { useQuery } from 'react-query';
import { getFrontendErrorReports, getScoreCards, getChartData, getCrashReportDetails } from '@/services/appCrashReport';
import type { FilterType } from '@/types/table';

/**
 * React Query hook for fetching paginated frontend error reports
 * Supports filtering, sorting, and searching
 * Used by App Crash Report table component
 */
export const useGetFrontendErrorReports = ({
	page = 1,
	limit = 15,
	search = '',
	dateRange,
	type,
	enabled = true,
}: FilterType) => {
	return useQuery({
		queryKey: [
			'frontend-error-reports',
			page,
			limit,
			search,
			dateRange,
			type,
		],
		queryFn: () =>
			getFrontendErrorReports(
				page,
				limit,
				search,
				dateRange,
				type,
			),
		enabled,
		keepPreviousData: false,
	});
};

/**
 * React Query hook for fetching score cards data
 * Used by App Crash Report overview component
 */
export const useGetScoreCards = () => {
	return useQuery({
		queryKey: ['score-cards'],
		queryFn: () => getScoreCards(),
		keepPreviousData: true,
	});
};

/**
 * React Query hook for fetching chart data
 * Used by App Crash Report overview component
 */
export const useGetChartData = (startDate?: string, endDate?: string) => {
	return useQuery({
		queryKey: ['chart-data', startDate, endDate],
		queryFn: () => getChartData(startDate, endDate),
		keepPreviousData: true,
	});
};

/**
 * React Query hook for fetching crash report details by ID
 * Used by App Crash Report details modal
 */
export const useGetCrashReportDetails = (id: string | null, enabled = false) => {
	return useQuery({
		queryKey: ['crash-report-details', id],
		queryFn: () => getCrashReportDetails(id as string),
		enabled: enabled && !!id,
		keepPreviousData: false,
	});
};

